import { cn } from '@virginexperiencedays/tailwind';

import { LinkWrap } from '../../navigation/LinkWrap';
import { LinkButton } from '../../navigation/LinkButton';
import { BodyText } from '../../typography/BodyText';
import { QuickBasketPromo } from './QuickBasketPromo';

import { roundPrice } from '../../utils/roundPrice';
import { subtotalValues } from './utils/subtotalValues';

import type { BasketItem } from '@virginexperiencedays/basket';

const DY_QB_PROMO_ATTR = 'qb-promo';

type Props = {
  items: BasketItem[];
  homeLink?: string;
  contentOverflow?: boolean;
  handleIsOpen?: (isOpen: boolean) => void;
  hasLoyaltyCta?: boolean;
  onLoyaltyCheckoutClick?: () => void;
  onLoyaltyEditBasketClick?: () => void;
};

export const QuickBasketFooter = ({
  items = [],
  homeLink,
  contentOverflow,
  handleIsOpen,
  hasLoyaltyCta,
  onLoyaltyCheckoutClick,
  onLoyaltyEditBasketClick,
}: Props) => {
  const { totalCost, totalSavings, totalItems, isPartialDiscount } = subtotalValues(items);

  const itemsText = totalItems === 1 ? 'item' : 'items';

  const handleCheckoutClick = () => {
    if (hasLoyaltyCta && onLoyaltyCheckoutClick) {
      onLoyaltyCheckoutClick?.();
    }
    handleIsOpen?.(false);
  };

  const handleEditBasketClick = () => {
    if (hasLoyaltyCta && onLoyaltyEditBasketClick) {
      onLoyaltyEditBasketClick?.();
    }
    handleIsOpen?.(false);
  };

  return (
    <footer
      className={cn(
        'bg-background-elevation-raised sticky bottom-0 right-0 mt-auto flex flex-col flex-nowrap gap-4 pb-4',
        contentOverflow &&
          'before:absolute before:-left-4 before:-right-4 before:-top-6 before:h-6 before:bg-black before:bg-[linear-gradient(0deg,_rgba(0,_0,_0,_0.075)_0%,_rgba(255,_255,_255,_0)_100%)]'
      )}
    >
      <div className="bg-background-elevation-raised -mx-4 px-4">
        <hr className="border-t-background-neutral-faded m-0 w-full border-t" />
        <div className="mt-4">
          <QuickBasketPromo
            data-dy={DY_QB_PROMO_ATTR}
            isPartialDiscount={isPartialDiscount}
            className="hidden"
          />
          <div data-dy={DY_QB_PROMO_ATTR} className="hidden">
            {totalSavings > 0 && (
              <div className="mb-4 flex w-full items-end">
                <BodyText
                  leading="none"
                  className="flex-1 font-semibold text-[#34A3E7] transition-opacity"
                >
                  Savings
                </BodyText>
                <BodyText
                  size="large"
                  leading="none"
                  className="font-bold text-[#34A3E7] [text-rendering:geometricprecision]"
                >
                  {roundPrice(totalSavings)}
                </BodyText>
              </div>
            )}
          </div>
          <div className="flex flex-col flex-nowrap gap-4">
            <div className="flex items-end">
              <BodyText
                leading="none"
                className="text-neutral-strong flex-1 font-semibold transition-opacity"
              >
                Subtotal ({totalItems} {itemsText})
              </BodyText>
              <BodyText
                size="large"
                leading="none"
                className="text-neutral-strong font-bold [text-rendering:geometricprecision]"
              >
                {roundPrice(totalCost)}
              </BodyText>
            </div>
            <BodyText size="small" className="text-neutral-faded block text-xs">
              <span>Delivery calculated at checkout </span>
              <LinkWrap
                href={`${homeLink}/packaging-delivery`}
                isExternal
                target="_blank"
                className="text-inherit"
                rel="nofollow"
              >
                find out more
              </LinkWrap>
            </BodyText>
            <LinkButton
              href={`${homeLink}/checkout/express`}
              variant="primary"
              size="xl"
              isExternal
              target="_self"
              onClick={handleCheckoutClick}
              rel="nofollow"
            >
              Checkout now
            </LinkButton>
            <LinkWrap
              className="text-neutral-strong p-2 text-center text-sm font-semibold"
              href={`${homeLink}/basket`}
              isExternal
              target="_self"
              onClick={handleEditBasketClick}
              rel="nofollow"
            >
              Edit Basket / Add Gift Message
            </LinkWrap>
          </div>
        </div>
      </div>
    </footer>
  );
};
